import type {
  CombinedCampaign,
  TourOperatorDiscountCampaign,
  UseCombinedCampaignsContext,
} from '@/modules/common/hooks/useCampaigns/types';
import type {Breakpoint as BreakpointType} from '@travel-frontend/themes';

import {Breakpoint} from '@travel-frontend/core';
import {IconChevronRight} from '@travel-frontend/icons';
import {
  CashbackInterceptor,
  CashbackInterceptorSize,
  SPACING_SMALL,
  Stack,
  useBooleanToggle,
} from '@travel-frontend/ui-components';
import {useCallback, useMemo} from 'react';

import {Background} from '@/components/CampaignBanners/components/Background';
import {Container} from '@/components/CampaignBanners/components/Container';
import {HintElement} from '@/components/CampaignBanners/components/HintElement';
import {OverlayTrigger} from '@/components/CampaignBanners/components/OverlayTrigger';
import {Subtitle} from '@/components/CampaignBanners/components/Subtitle';
import {DiscountCampaignBanner} from '@/components/CombinedCampaignBanner/components/DiscountCampaignButton';
import {TextWrapper} from '@/components/CombinedCampaignBanner/components/TextWrapper';
import {ConditionsLayer} from '@/components/ConditionsLayer';

type Props = {
  campaign: CombinedCampaign;
  discountCampaign: TourOperatorDiscountCampaign;
  onClick: UseCombinedCampaignsContext['handleTourOperatorDiscountCampaignBannerClick'];
};

export const CombinedCampaignBanner: React.FunctionComponent<Props> = ({
  campaign,
  discountCampaign,
  onClick,
}) => {
  const [isOpen, setIsOpen] = useBooleanToggle();
  const handleOpen = useCallback(() => setIsOpen(true), [setIsOpen]);
  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);

  const [from, to] = campaign.gradient.split(',');

  const cashbackTextElements = useMemo(
    () =>
      (['xs', 'sm', 'md', 'lg', 'xl'] as Array<BreakpointType>).map((bp) => {
        const titleText = campaign.cashbackTitle[bp];
        const linkText = campaign.cashbackLink[bp];

        return (
          <Breakpoint key={bp} matches={[bp]}>
            <TextWrapper>
              {titleText && <Subtitle value={titleText} />}
              {linkText && (
                <OverlayTrigger type="button" onClick={handleOpen}>
                  {linkText}
                  <IconChevronRight />
                </OverlayTrigger>
              )}
            </TextWrapper>
          </Breakpoint>
        );
      }),
    [campaign.cashbackLink, campaign.cashbackTitle, handleOpen]
  );

  const discountTextElements = useMemo(
    () =>
      (['xs', 'sm', 'md', 'lg', 'xl'] as Array<BreakpointType>).map((bp) => {
        const titleText = campaign.discountTitle[bp];

        return (
          <Breakpoint key={bp} matches={[bp]}>
            {titleText && <Subtitle value={titleText} />}
          </Breakpoint>
        );
      }),
    [campaign.discountTitle]
  );

  return (
    <Container
      gradient={campaign.gradient}
      direction="row"
      alignItems="center"
      justifyContent="center"
    >
      <Background from={from} to={to} />
      <Breakpoint matches={['xs', 'sm']}>
        <CashbackInterceptor
          text={campaign.interceptorText}
          highlight={campaign.interceptorHighlight}
          size={CashbackInterceptorSize.MEDIUM}
        />
        {cashbackTextElements}
      </Breakpoint>
      <Breakpoint matches={['md', 'lg', 'xl']}>
        {cashbackTextElements}
        <Stack alignItems="center" spacing={SPACING_SMALL}>
          <CashbackInterceptor
            text={campaign.interceptorText}
            highlight={campaign.interceptorHighlight}
            size={CashbackInterceptorSize.LARGE}
          />
          <HintElement
            hint={campaign.hint}
            hintTooltip={campaign.hintTooltip}
          />
        </Stack>
        <TextWrapper>
          {discountTextElements}
          <DiscountCampaignBanner onClick={() => onClick(discountCampaign)}>
            {campaign.discountButtonText}
          </DiscountCampaignBanner>
        </TextWrapper>
      </Breakpoint>
      <ConditionsLayer
        identifier={campaign.conditionsId}
        open={isOpen}
        onClose={handleClose}
      />
    </Container>
  );
};
